import SeoContext from '../../lib/seo-context';
import { fetchAPI } from '../../lib/api';

import Layout from '../../components/layout';
import HeroSubpage from '../../components/hero-subpage';

import { Merge, estimateTime, formatDate } from '../../lib/helper';

import { useGlobalContext } from '../../lib/global-context';
import BlogArticleList from '../../components/blog-articles-list';
import Pagination from '../../components/pagination';
import BlogCategoryList from '../../components/blog-category-list';
import RssFeed from '../../components/rss-feed';
import WriteForUs from '../../components/write-for-us';

const Page = (props) => {
    const { state } = useGlobalContext();

    const global = state;
    const { pageProps } = props;

    if (!pageProps) {
        return '';
    }

    const seo = pageProps && pageProps.seo ? Merge(global.seo, pageProps.seo) : global.seo;

    seo.canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/blog/${pageProps.page}`;

    seo.metaTitle += ` - Page ${pageProps.page}`;
    seo.metaDescription += ` - Page ${pageProps.page}`;

    return (
        <SeoContext.Provider value={seo}>
            <Layout
                footerClass={'skew-maturity-100'}
                languageData={{ originalSlug: 'blog', languages: pageProps.localizations }}
            >
                <HeroSubpage full={true} key={'hero-subpage'} {...pageProps.hero} />

                <section key="blog-articles" className="container mx-auto content">
                    <div className="flex flex-col lg:flex-row p-10">
                        <div className="flex flex-col w-full lg:w-2/3">
                            <div className="articles">
                                <BlogArticleList {...pageProps.articles} />
                            </div>
                            <div className="flex mt-10">
                                <Pagination
                                    url={`/blog`}
                                    pagination={pageProps.articles.meta.pagination}
                                ></Pagination>
                            </div>
                        </div>
                        <div className="flex flex-col w-full lg:w-1/3 lg:pl-10  relative">
                            <div className="sticky top-32 w-full">
                                <BlogCategoryList selected="" categories={pageProps.categories} />
                                <RssFeed />
                                <WriteForUs />
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </SeoContext.Provider>
    );
};

export async function getStaticPaths(ctx) {
    const paths = [];

    const { locale } = ctx;

    // get blog settings
    const settings = await fetchAPI('/blog', {
        populate: 'deep,2',
        locale
    });

    const pageSize = settings && settings.data ? settings.data.attributes.articlePerPage : 6;

    // get articles
    const res = await fetchAPI('/blog-articles', {
        sort: ['id:desc'],
        populate: 'deep,2',
        locale
    });

    const totalPages = Math.ceil(res.data.length / pageSize);
    for (let i = 1; i <= totalPages; i++) {
        paths.push({ params: { slug: 'blog', page: i.toString() }, locale });
    }

    return {
        paths: paths,
        fallback: true
    };
}

export async function getStaticProps(ctx) {
    const { params, locale } = ctx;

    const page = params.page ? params.page : 1;

    try {
        const result = await fetchAPI(`/blog`, {
            populate: 'deep,3',
            locale: locale
        });

        if (!result) {
            return { notFound: true };
        }
        const pageSize = result.data.attributes.articlePerPage;

        result.data.attributes.page = page;

        const articles = await fetchAPI(`/blog-articles`, {
            populate: 'deep,3',
            sort: ['id:desc'],
            locale: locale,
            pagination: {
                page,
                pageSize
            }
        });

        articles.data.map((a, index) => {
            articles.data[index].attributes.estimate = estimateTime(a.attributes.content);
            articles.data[index].attributes.date = formatDate(a.attributes.createdAt);
        });
        result.data.attributes.articles = articles;

        const categoriesData = await fetchAPI(`/blog-categories`, {
            populate: 'deep,3',
            sort: ['title:asc'],
            locale: locale
        });
        const categories = [];
        categoriesData.data.map((cat) => {
            categories.push({
                id: cat.id,
                slug: cat.attributes.slug,
                title: cat.attributes.title,
                description: cat.attributes.title,
                seo: cat.attributes.seo,
                articleCount: cat.attributes.blog_articles.data.length
            });
        });

        result.data.attributes.categories = categories;

        return {
            props: { pageProps: result.data.attributes },
            revalidate: 1
        };
    } catch (e) {
        console.log('got an error', e.message);
        return { notFound: true };
    }
}

export default Page;
